import React, { useEffect, useRef, useState } from "react";
import { FileText, CaretDown, CaretUp, ArrowSquareOut, ArrowsOutSimple, Phone } from "@phosphor-icons/react";
import { fetchNwccBrief } from "../lib/api";
import { shareOrigin } from "../lib/shareOrigin";
import InlineShareButton from "./InlineShareButton";
import BriefPopout from "./BriefPopout";

const PL_COLORS = { 1: "#2A9D8F", 2: "#4C9AFF", 3: "#FFB347", 4: "#F2542D", 5: "#E63946" };
const PDF_URL = `${process.env.REACT_APP_BACKEND_URL}/api/nwcc-brief.pdf`;

function PlChip({ label, value }) {
  if (value == null) return null;
  const color = PL_COLORS[value] || "#8892A0";
  return (
    <div className="flex flex-col items-center px-2 py-1 border rounded-sm min-w-[58px]"
      style={{ borderColor: `${color}55`, background: `${color}12` }}>
      <span className="text-[10px] uppercase tracking-wider text-tac-faint">{label}</span>
      <span className="text-sm font-black font-mono-tac" style={{ color }}>{value}</span>
    </div>
  );
}

export default function NwccBriefCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [popout, setPopout] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    let stop = false;
    fetchNwccBrief()
      .then((d) => { if (!stop) setData(d); })
      .catch(() => {});
    return () => { stop = true; };
  }, []);

  useEffect(() => {
    if (!data?.available) return;
    if (new URLSearchParams(window.location.search).get("brief") === "nwcc") {
      setPopout(true);
      setOpen(true);
      setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [data?.available]);

  if (!data?.available) return null;
  const pl = data.pl || {};
  const waIncidents = (data.incidents || []).filter((i) => i.state === "WA");
  const orIncidents = (data.incidents || []).filter((i) => i.state === "OR");

  const caption = [
    `🔥 NWCC Morning Brief${data.date ? ` — ${data.date}` : ""}`,
    pl.current != null ? `Northwest Preparedness Level ${pl.current}` : null,
    data.nw_fires != null
      ? `${data.nw_fires} new fires for ${data.nw_acres} acres in the Northwest${data.wa_fires != null ? ` (WA ${data.wa_fires} · OR ${data.or_fires})` : ""}`
      : null,
    data.acres_growth ? `${data.acres_growth} acres growth on existing incidents` : null,
    `Full brief: ${PDF_URL}`,
    `Read it on the live fire map: ${shareOrigin()}/?brief=nwcc`,
  ].filter(Boolean).join("\n");

  const body = (
    <div className="px-4 pb-3 space-y-3">
      {data.date && (
            <div className="text-[11px] font-mono-tac uppercase tracking-wider text-tac-faint">{data.date}</div>
          )}

          <div className="flex flex-wrap gap-1.5">
            <PlChip label="NW PL" value={pl.current} />
            <PlChip label="3-Day" value={pl.three_day} />
            <PlChip label="10-Day" value={pl.ten_day} />
            <PlChip label="National" value={pl.national} />
          </div>

          <div className="text-[13px] text-tac-dim space-y-0.5">
            {data.nw_fires != null && (
              <div><b className="text-tac-text">{data.nw_fires} new fires</b> for {data.nw_acres} acres in the Northwest
                {data.wa_fires != null ? <> — WA {data.wa_fires} · OR {data.or_fires}</> : null}
              </div>
            )}
            {data.acres_growth && (
              <div><b className="text-tac-text">{data.acres_growth} acres</b> growth on existing incidents</div>
            )}
            {data.reported_incidents && (
              <div>Large incidents reported: <b className="text-tac-text">{data.reported_incidents}</b></div>
            )}
          </div>

          {data.activity_summary && (
            <p className="text-[13px] leading-relaxed text-tac-dim">{data.activity_summary}</p>
          )}

          {(waIncidents.length > 0 || orIncidents.length > 0) && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-tac-primary mb-1">
                Large Incidents ({(data.incidents || []).length})
              </div>
              <div className="border border-tac-border rounded-sm overflow-hidden">
                {[...waIncidents, ...orIncidents].map((f, i) => (
                  <div key={i} className={i % 2 ? "bg-tac-surface/50" : ""}>
                    <button
                      type="button"
                      data-testid={`nwcc-incident-${i}`}
                      onClick={() => setExpanded(expanded === i ? null : i)}
                      className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-[12px] text-left hover:bg-tac-surface/70 transition-colors"
                    >
                      <span className="font-bold text-tac-text truncate">
                        <span className={f.state === "WA" ? "text-tac-primary mr-1" : "text-tac-faint mr-1"}>{f.state}</span>
                        {f.name}
                      </span>
                      <span className="font-mono-tac text-tac-dim shrink-0">
                        {f.acres ? `${f.acres} ac` : ""}
                        {f.growth ? ` (${f.growth})` : ""}
                        {f.pct_contained != null ? ` · ${f.pct_contained}%` : ""}
                      </span>
                    </button>
                    {expanded === i && (
                      <p className="px-2.5 pb-2 text-[12px] leading-relaxed text-tac-dim">{f.detail}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.imt_rotation?.teams?.length > 0 && (
            <div data-testid="nwcc-imt-rotation" className="text-[12px] text-tac-dim">
              <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-tac-primary">
                IMT Rotation{data.imt_rotation.window ? ` (${data.imt_rotation.window})` : ""}:
              </span>{" "}
              {data.imt_rotation.teams.map((t, i) => (
                <span key={t}>
                  {i === 0 ? <b className="text-tac-text">{t} — up next</b> : t}
                  {i < data.imt_rotation.teams.length - 1 ? " · " : ""}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <a
              href={PDF_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="nwcc-brief-pdf-link"
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-tac-primary hover:underline"
            >
              Open Full Brief (PDF) <ArrowSquareOut size={11} />
            </a>
            <a
              href="tel:+15038082764"
              data-testid="nwcc-pio-phone"
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-tac-dim hover:text-tac-primary"
            >
              <Phone size={11} weight="fill" /> Public Info: (503) 808-2764
            </a>
          </div>
    </div>
  );

  const plBadge = pl.current != null && (
    <span
      data-testid="nwcc-pl-badge"
      className="px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border shrink-0"
      style={{
        color: PL_COLORS[pl.current] || "#8892A0",
        borderColor: `${PL_COLORS[pl.current] || "#8892A0"}55`,
        background: `${PL_COLORS[pl.current] || "#8892A0"}15`,
      }}
    >
      NW PL {pl.current}
    </span>
  );

  return (
    <section ref={sectionRef} data-testid="nwcc-brief-card" className="border-b border-tac-border bg-tac-surface/40">
      <div className="flex items-center">
        <button
          type="button"
          data-testid="nwcc-brief-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex-1 min-w-0 flex items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-tac-surface/60 transition-colors"
        >
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <FileText size={14} weight="fill" className="text-tac-primary" />
            <span className="text-[12px] uppercase tracking-[0.15em] font-bold text-tac-text">
              NWCC Morning Brief
            </span>
            {plBadge}
          </div>
          {open ? <CaretUp size={12} className="text-tac-dim shrink-0" /> : <CaretDown size={12} className="text-tac-dim shrink-0" />}
        </button>
        <div className="pr-3 shrink-0 flex items-center gap-1.5">
          <button
            type="button"
            data-testid="nwcc-popout-btn"
            title="Open in large view"
            onClick={() => setPopout(true)}
            className="inline-flex items-center justify-center w-7 h-7 border border-tac-border hover:border-tac-primary hover:text-tac-primary text-tac-dim rounded-sm transition-colors"
          >
            <ArrowsOutSimple size={12} weight="bold" />
          </button>
          <InlineShareButton
            variant="compact"
            testid="nwcc-share-btn"
            url={`${shareOrigin()}/?brief=nwcc`}
            title="NWCC Morning Brief"
            text={caption}
            caption={caption}
          />
        </div>
      </div>

      {open && <div data-testid="nwcc-brief-body">{body}</div>}

      <BriefPopout open={popout} onClose={() => setPopout(false)} title="NWCC Morning Brief" badge={plBadge}>
        {body}
      </BriefPopout>
    </section>
  );
}
